/**
 * 用户状态管理 Store
 * 使用 Zustand 统一管理用户状态
 */
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { login as loginApi } from '../services/guest'
import { getBaseUrl } from '../services/api'

/**
 * 用户 Store
 */
const useUserStore = create(
  persist(
    (set, get) => ({
      // ============================================
      // 状态
      // ============================================
      user: null,
      config: { baseUrl: '', clients: {} },
      isLoading: true, // 初始化加载中
      error: null,

      // ============================================
      // 计算属性
      // ============================================
      get isLoggedIn() {
        return !!get().user?.token
      },

      // ============================================
      // Actions
      // ============================================
      
      /**
       * 初始化应用
       * - 加载 config.json
       * - 恢复用户登录状态
       */
      initialize: async () => {
        const state = get()
        
        // 避免重复初始化
        if (state.config.baseUrl && !state.isLoading) {
          return
        }

        try {
          // 加载配置
          const response = await fetch('/config.json')
          const configData = await response.json()
          
          // 检查本地存储的用户数据
          const savedToken = localStorage.getItem('v2board_token')
          const savedEmail = localStorage.getItem('v2board_email')
          
          if (savedToken && savedEmail && configData.baseUrl) {
            set({
              config: configData,
              user: {
                email: savedEmail,
                token: savedToken,
                baseUrl: configData.baseUrl,
              },
              isLoading: false,
              error: null,
            })
          } else {
            set({
              config: configData,
              user: null,
              isLoading: false,
              error: null,
            })
          }
        } catch (error) {
          console.error('初始化失败:', error)
          set({
            isLoading: false,
            error: '应用初始化失败，请刷新页面重试',
          })
        }
      },

      /**
       * 用户登录
       */
      login: async (email, password) => {
        const { config } = get()
        
        // 确保配置已加载
        let baseUrl = config.baseUrl
        if (!baseUrl) {
          try {
            baseUrl = await getBaseUrl()
          } catch {
            throw new Error('无法获取服务器配置')
          }
        }

        set({ isLoading: true, error: null })

        try {
          const result = await loginApi(email, password)
          
          if (result?.auth_data) {
            const userData = {
              email,
              token: result.auth_data,
              baseUrl,
            }
            
            // 保存到本地存储
            localStorage.setItem('v2board_token', result.auth_data)
            localStorage.setItem('v2board_email', email)
            
            set({
              user: userData,
              isLoading: false,
              error: null,
            })
            
            return userData
          } else {
            throw new Error('登录成功，但未返回认证凭证')
          }
        } catch (error) {
          const errorMessage = error?.message || '登录失败，请重试'
          set({
            isLoading: false,
            error: errorMessage,
          })
          throw error
        }
      },

      /**
       * 用户登出
       */
      logout: () => {
        // 清除本地存储
        localStorage.removeItem('v2board_token')
        localStorage.removeItem('v2board_email')
        localStorage.removeItem('v2board_user')
        localStorage.removeItem('v2board_url')
        
        set({
          user: null,
          error: null,
        })
      },

      /**
       * 更新用户数据
       */
      updateUserData: (newData) => {
        set((state) => ({
          user: state.user ? { ...state.user, ...newData } : null,
        }))
      },

      /**
       * 清除错误
       */
      clearError: () => {
        set({ error: null })
      },

      /**
       * 设置加载状态
       */
      setLoading: (isLoading) => {
        set({ isLoading })
      },
    }),
    {
      name: 'v2board-user-storage',
      partialize: (state) => ({
        // 只持久化必要的数据
        config: state.config,
      }),
    }
  )
)

export default useUserStore
